import styled from "@emotion/styled";
import { useCallback, type CSSProperties, type MouseEvent } from "react";

import { SkiliticsIcon, ToggleButton, ToggleButtonGroup, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { setIndividualBorderProperties, setSingleBorderProperties } from "./utils";

const StyledToggleButton = styled(ToggleButton)`
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px !important;
  height: 24px;
  width: 24px;
  font-size: 15px;
  padding: 0;
  margin-right: 5px;
`;

export type BorderType = "single" | "individual";

type Props = {
  borderType: BorderType;
  // onBorderTypeChange: (borderType: BorderType) => void;
  cssProperties: CSSProperties;
  onCssPropertiesChange?: (values: Partial<CSSProperties>) => void;
};

export const BorderTypePicker = ({ borderType, cssProperties, onCssPropertiesChange }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const handleBorderTypeChange = useCallback(
    (_: MouseEvent<HTMLElement>, value: BorderType) => {
      if (value && value !== borderType) {
        // Reset Border Styles when switching.

        if (value === "individual") {
          // Set border styles to match the single border when switching to individual borders.
          onCssPropertiesChange?.({
            ...setSingleBorderProperties(undefined, undefined),
            ...setIndividualBorderProperties(cssProperties.borderWidth ?? "1px", cssProperties.borderColor ?? "#000"),
          });
        } else if (value === "single") {
          // Set border styles to the top border if set, or back to default.
          onCssPropertiesChange?.({
            ...setSingleBorderProperties(cssProperties.borderTopWidth ?? "1px", cssProperties.borderTopColor ?? "#000"),
            ...setIndividualBorderProperties(undefined, undefined),
          });
        }
      }
    },
    [
      borderType,
      cssProperties.borderColor,
      cssProperties.borderTopColor,
      cssProperties.borderTopWidth,
      cssProperties.borderWidth,
      onCssPropertiesChange,
    ],
  );

  return (
    <ToggleButtonGroup value={borderType} onChange={handleBorderTypeChange} color="primary" exclusive>
      <Tooltip title={formatMessage("singleBorder")} placement="top">
        <StyledToggleButton value="single">
          <SkiliticsIcon icon="outline-single-value" />
        </StyledToggleButton>
      </Tooltip>
      <Tooltip title={formatMessage("individualBorders")} placement="top">
        <StyledToggleButton value="individual">
          <SkiliticsIcon icon="outline-individual" />
        </StyledToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
};

const formatStrings = {
  singleBorder: {
    en: "Style outline as one",
  },
  individualBorders: {
    en: "Style outline per side",
  },
};
